import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Dialog } from 'radix-ui';
import { useCompany } from '@/company/context';
import { useAppCopy } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import editorCopy from '@/i18n/ns/workspace-editors';
import { LockIcon, XIcon } from 'lucide-react';
import { useWorkspace } from '../context';
import { WorkspaceIcon } from '../icons';
import { SheetButton } from '../share-sheet/share-sheet';
import { projectsApi } from './api';
import { ProjectMark } from './mark';
import { chatProject, rememberProjectPlace, rememberThreadPlace } from './place';
import { closeNewProject, useNewProjectRequest } from './new-project-request';
import { putProject } from './use-projects';
import type { ProjectTint } from './types';
import { nameTint, ProjectTintPicker } from './tint-picker';

/** Mounted once in the shell; every way in opens the same sheet. */
export function NewProjectSheetHost() {
  const ws = useWorkspace();
  const companyId = useCompany().data?.company?.id;
  const current = useNewProjectRequest();
  useEffect(() => {
    closeNewProject();
    window.addEventListener('hashchange', closeNewProject);
    window.addEventListener('popstate', closeNewProject);
    return () => {
      window.removeEventListener('hashchange', closeNewProject);
      window.removeEventListener('popstate', closeNewProject);
      closeNewProject();
    };
  }, [ws.view, ws.threadId, ws.user?.id, companyId]);
  return (
    <Dialog.Root open={Boolean(current)} onOpenChange={(open) => !open && closeNewProject()}>
      {current && <NewProjectSheet key={current.key} request={current} />}
    </Dialog.Root>
  );
}

function NewProjectSheet({
  request: from,
}: {
  request: NonNullable<ReturnType<typeof useNewProjectRequest>>;
}) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const editor = useNamespaceCopy(editorCopy);
  const appCopy = useAppCopy();
  const [name, setName] = useState(from.name ?? '');
  const [brief, setBrief] = useState('');
  const [picked, setPicked] = useState<ProjectTint | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const trimmed = name.trim();
  const tint = picked ?? nameTint(trimmed);
  const fromChat = Boolean(from.chatId);
  const canDraft = fromChat && !chatProject(ws);
  const hasDraft = ws.hasComposerDraft?.() ?? false;
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const create = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!trimmed || busy) return;
    setBusy(true);
    setFailed(false);
    const stillHere = ws.beginNavigation();
    try {
      const project = await projectsApi.create({
        name: trimmed,
        tint,
        ...(brief.trim() ? { brief: brief.trim() } : {}),
        ...(from.chatId ? { chatId: from.chatId } : {}),
      });
      if (!active.current || !stillHere()) return;
      const place = { id: project.id, name: project.name, tint: project.tint };
      rememberProjectPlace(place);
      putProject(project);
      closeNewProject();
      if (from.chatId) {
        rememberThreadPlace(from.chatId, place);
        const { projectId: _placed, newProject: _drafting, ...rest } = ws.turnContext;
        ws.setTurnContext(rest);
        ws.refreshThreadMeta();
        ws.notify(
          formatCopy(copy.projectReadyChat, { name: project.name }),
          { label: copy.openProject, run: () => ws.openProject(project.id) },
          'success',
        );
      } else {
        ws.openProject(project.id);
        ws.notify(formatCopy(copy.projectReadyPrivate, { name: project.name }), null, 'success');
      }
    } catch {
      setFailed(true);
      setBusy(false);
    }
  };

  // The model's way, kept for a chat it can read: it drafts the brief beside the chat.
  const writeFromChat = () => {
    if (!canDraft || busy || ws.hasComposerDraft?.()) return;
    closeNewProject();
    const title = trimmed
      ? `${formatCopy(editor.makeChatProjectNamed, { name: trimmed })}`
      : editor.makeChatProject;
    ws.send(
      brief.trim()
        ? `${title}\n\n${formatCopy(editor.projectBriefInstruction, { brief: brief.trim() })}`
        : title,
      { ...ws.turnContext, newProject: true },
      true,
    );
  };

  return (
    <Dialog.Portal>
      <Dialog.Overlay className="ws-sharesheet-scrim" />
      <Dialog.Content
        className="ws-sharesheet ws-new-project"
        data-testid="new-project-sheet"
        aria-describedby={undefined}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (location.hash === from.route && from.returnFocus?.isConnected)
            from.returnFocus.focus();
        }}
        onEscapeKeyDown={(event) => busy && event.preventDefault()}
        onInteractOutside={(event) => busy && event.preventDefault()}
      >
        <form onSubmit={(event) => void create(event)}>
          <header className="ws-sharesheet-head">
            {trimmed ? (
              <ProjectMark name={trimmed} tint={tint} size="lg" />
            ) : (
              <span className="ws-sharesheet-icon" aria-hidden="true">
                <WorkspaceIcon name="project" size={20} />
              </span>
            )}
            <div className="ws-sharesheet-heading">
              <span className="ws-sharesheet-eyebrow">{copy.projects}</span>
              <Dialog.Title className="ws-sharesheet-title">{copy.newProject}</Dialog.Title>
            </div>
            <Dialog.Close
              className="ws-sharesheet-close"
              aria-label={appCopy.close}
              disabled={busy}
            >
              <XIcon aria-hidden="true" />
            </Dialog.Close>
          </header>
          <div className="ws-sharesheet-body ws-new-project-body">
            {fromChat && (
              <div className="ws-new-project-chat">
                <span className="ws-project-mark sm" data-tint="plain" aria-hidden="true">
                  <WorkspaceIcon name="chat" size={12} />
                </span>
                <span>
                  <b>{from.chatTitle || copy.thisChat}</b>
                  <small>{copy.thisChatMoves}</small>
                </span>
                <WorkspaceIcon name="check" size={15} />
              </div>
            )}
            <div>
              <label className="ws-field-label" htmlFor="new-project-name">
                {copy.nameLabel}
              </label>
              <input
                id="new-project-name"
                className="ws-field"
                placeholder={copy.projectNameExample}
                maxLength={80}
                autoFocus
                autoComplete="off"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div>
              <div className="ws-new-project-label">
                <label className="ws-field-label" htmlFor="new-project-brief">
                  {copy.whatIsWork} <span>{copy.optional}</span>
                </label>
                {canDraft && (
                  <button
                    type="button"
                    className="ws-text-button"
                    onClick={writeFromChat}
                    disabled={busy || hasDraft}
                    aria-describedby={hasDraft ? 'new-project-draft-help' : undefined}
                  >
                    <WorkspaceIcon name="chat" size={13} />
                    {copy.writeFromChat}
                  </button>
                )}
              </div>
              <textarea
                id="new-project-brief"
                className="ws-field"
                rows={4}
                maxLength={8000}
                placeholder={copy.briefPlaceholder}
                value={brief}
                onChange={(event) => setBrief(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) void create();
                }}
              />
              <p className="ws-field-help">
                {canDraft ? copy.briefDraftHelp : copy.briefLaterHelp}
              </p>
              {canDraft && hasDraft && (
                <p className="ws-field-help" id="new-project-draft-help">
                  {copy.sendClearFirst}
                </p>
              )}
            </div>
            <ProjectTintPicker value={tint} onChange={setPicked} disabled={busy} />
          </div>
          <footer className="ws-sharesheet-foot">
            <span className="ws-sharesheet-status">
              {failed ? (
                <span role="alert">{copy.createProjectError}</span>
              ) : (
                <>
                  <LockIcon aria-hidden="true" />
                  {copy.onlyYouUntil}
                </>
              )}
            </span>
            <div className="ws-sharesheet-actions">
              <Dialog.Close asChild>
                <SheetButton disabled={busy}>{appCopy.cancel}</SheetButton>
              </Dialog.Close>
              <SheetButton primary type="submit" disabled={!trimmed || busy}>
                {busy ? copy.creating : copy.createProject}
              </SheetButton>
            </div>
          </footer>
        </form>
      </Dialog.Content>
    </Dialog.Portal>
  );
}
